import { Group } from '@mantine/core';
import { useCallback, useState } from 'react';
import BundleActions from './BundleActions';
import ConnectorsListScreen, { kindOf } from './ConnectorsListScreen';
import { apiBaseFor } from './shared';
import ConnectorWorkspace from './workspace/ConnectorWorkspace';

const ConnectorScreens = ({ apiBase, accountId, domainId }) => {
  // The form screen unmounts as the tab switches, so the loaded rows are held here: the form is
  // handed the row it was opened on and does not read the instance.
  const [screen, setScreen] = useState({ name: 'list' });
  // Store tools or platform capabilities; kept here so Back returns to the list the reader was on.
  const [kind, setKind] = useState('tool');
  const [instances, setInstances] = useState([]);
  const [refreshKey, setRefreshKey] = useState(0);
  const onChanged = useCallback(() => setRefreshKey((key) => key + 1), []);

  const rowFor = (id) => instances.find((instance) => instance.id === id) || null;

  // The list is unmounted while the form is open, so the saved row is merged in here; the key bump
  // is what the list refetches on once the reader goes back to it.
  const onSaved = useCallback(
    (saved) => {
      setInstances((rows) => [...rows.filter((row) => row.id !== saved.id), saved]);
      onChanged();
      setScreen((previous) => ({ ...previous, id: saved.id }));
    },
    [onChanged]
  );

  const backToList = () => setScreen({ name: 'list' });

  if (screen.name === 'form') {
    // The kind is fixed when the screen opens: a saved connector never changes kind.
    return (
      <ConnectorWorkspace
        kind={screen.kind}
        apiBase={apiBase}
        accountId={accountId}
        domainId={domainId}
        instance={rowFor(screen.id)}
        onBack={backToList}
        onSaved={onSaved}
      />
    );
  }

  return (
    <>
      <Group justify="flex-end" mt="sm">
        <BundleActions apiBase={apiBase} accountId={accountId} domainId={domainId} onChanged={onChanged} />
      </Group>

      <ConnectorsListScreen
        apiBase={apiBase}
        refreshKey={refreshKey}
        kind={kind}
        onKindChange={setKind}
        onCreate={(next) => setScreen({ name: 'form', id: null, kind: next })}
        onLoaded={setInstances}
        onChanged={onChanged}
        onEdit={(instance) => setScreen({ name: 'form', id: instance.id, kind: kindOf(instance) })}
      />
    </>
  );
};

// The account dropdown rewrites the pathname in place and this element is reused, so a form open on
// one store's connector would keep showing it under the next store's heading.
const ConnectorsTab = ({ accountId, domainId }) => {
  const apiBase = apiBaseFor(accountId, domainId);

  return <ConnectorScreens key={apiBase} apiBase={apiBase} accountId={accountId} domainId={domainId} />;
};

export default ConnectorsTab;
